import React, { useEffect, useState } from "react";
import Joyride, { CallBackProps, EVENTS, STATUS, type Step } from "react-joyride";
import { useAppStore } from "../../store";
import { useThemeStore } from "../../store/theme";

const CustomTooltip = (props: any) => {
  const {
    index,
    step,
    backProps,
    primaryProps,
    skipProps,
    tooltipProps,
    isLastStep,
    size,
  } = props;
  const { theme, colors } = useThemeStore();

  return (
    <div
      {...tooltipProps}
      className={`max-w-sm w-[350px] rounded-2xl overflow-hidden p-6 relative ${
        theme === "dark" 
          ? "bg-[#1A1A1A]/95 backdrop-blur-xl border border-white/10 text-white" 
          : "bg-white/95 backdrop-blur-xl border border-gray-200 text-gray-900"
      }`}
      style={{
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)",
      }}
    >
      {/* Decorative colored orb */}
      <div 
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[40px] opacity-30 pointer-events-none" 
        style={{ backgroundColor: colors.primary }}
      />
      
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-2">
          <h3 
            className="text-[18px] font-bold"
            style={{ color: colors.primary }}
          >
            {step.title || `Step ${index + 1}`}
          </h3>
          {!isLastStep && (
            <button
              {...skipProps}
              className={`text-[13px] font-medium px-2.5 py-1 rounded-md transition-colors ${
                theme === "dark" 
                  ? "text-gray-400 hover:text-white hover:bg-white/10" 
                  : "text-gray-500 hover:text-gray-900 hover:bg-black/5"
              }`}
            >
              Skip
            </button>
          )}
        </div>
        <p className={`text-[14px] leading-relaxed mb-6 ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
          {step.content}
        </p>

        <div className="flex items-center justify-between mt-8">
          <div className="flex gap-1.5">
            {Array.from({ length: size }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index 
                    ? "w-5" 
                    : theme === "dark" ? "w-1.5 bg-gray-700" : "w-1.5 bg-gray-300"
                }`}
                style={i === index ? { backgroundColor: colors.primary } : {}}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                {...backProps}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  theme === "dark" 
                    ? "text-gray-400 hover:text-white hover:bg-white/10" 
                    : "text-gray-500 hover:text-gray-900 hover:bg-black/5"
                }`}
              >
                Back
              </button>
            )}
            <button
              {...primaryProps}
              className="px-5 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-all shadow-lg active:scale-95"
              style={{ 
                backgroundColor: colors.primary,
                boxShadow: `0 4px 14px 0 ${colors.primary}50` 
              }}
            >
              {isLastStep ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractiveTour: React.FC = () => {
  const { runTutorial, setRunTutorial, setHasSeenTutorial, hasSeenTutorial, isLoggedIn } = useAppStore();

  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (isLoggedIn && !hasSeenTutorial && !runTutorial) {
      setRunTutorial(true);
    }
  }, [isLoggedIn, hasSeenTutorial, runTutorial, setRunTutorial]);

  useEffect(() => {
    setSteps([
      {
        target: "body",
        title: "Welcome to Idearify!",
        content: "Let's take a quick tour of the features.",
        placement: "center",
        disableBeacon: true,
      },
      {
        target: "#nav-discover",
        title: "Discover",
        content: "Here you can discover new and trending ideas from the community.",
        placement: "right",
      },
      {
        target: "#nav-the-café",
        title: "The Café",
        content: "Hang out with other creators, share ideas, and chat in real-time.",
        placement: "right",
      },
      {
        target: "#nav-inbox",
        title: "Inbox",
        content: "Check your direct messages and notifications.",
        placement: "right",
      },
      {
        target: "#nav-checklists",
        title: "Checklists",
        content: "Stay on top of your tasks and milestones with checklists.",
        placement: "right",
      },
      {
        target: "#nav-spark-co-pilot",
        title: "Spark Co-Pilot",
        content: "Use Spark Co-Pilot to generate new ideas and brainstorm with AI.",
        placement: "right",
      },
      {
        target: "#nav-profile",
        title: "Profile",
        content: "View and manage your public profile and your shared ideas.",
        placement: "right",
      },
      {
        target: "#nav-share-your-idea",
        title: "Share Your Idea",
        content: "Ready to share? Click here to upload and post your own ideas!",
        placement: "bottom",
      },
      {
        target: "#nav-credit-balance",
        title: "Spotlight Credits",
        content: "Check your balance of Spotlight Credits here. Use them to spotlight your ideas and boost their visibility!",
        placement: "bottom",
      },
      {
        target: "#nav-settings",
        title: "Settings",
        content: "Customize your experience and manage your account here.",
        placement: "right",
      },
    ]);
  }, []);

  const [stepIndex, setStepIndex] = useState(0);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, index, action } = data;
    
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (type === EVENTS.STEP_AFTER || type === EVENTS.ERROR) {
      setStepIndex(index + (action === "prev" ? -1 : 1));
    }

    if (finishedStatuses.includes(status)) {
      setRunTutorial(false);
      setHasSeenTutorial(true);
      setStepIndex(0);
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      run={runTutorial}
      stepIndex={stepIndex}
      scrollToFirstStep
      steps={steps}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        hideArrow: false,
      }}
      styles={{
        options: {
          zIndex: 10000,
          overlayColor: "rgba(0, 0, 0, 0.85)", // Darker overlay instead of blur to keep spotlight clear
        },
      }}
    />
  );
};

export default InteractiveTour;